<template>
    <div class="modal fade show my-2" aria-labelledby="addNewCardTitle" style="display: block; background: #1c1d1c33">
        <div class="modal-dialog modal-lg modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                          <h1 class="mb-0">Create Contra</h1>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="pb-2">
                  <div class="row mx-2 pt-2">

                    <div class="col-12 col-md-6 mb-1">
                      <label class="form-label" for="date"> <span class="required">*</span> Date </label>
                      <input
                          class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                          type="date"
                          v-model="formData.date"
                      >
                    </div>
                    <div class="col-12 col-md-6 mb-1" v-if="headId">
                      <label class="form-label" for="paidFrom"> Transfer From / Paid From </label>
                      <input
                          class="form-control"
                          type="text"
                          :value="headId.name"
                          disabled
                      >
                    </div>

                    <div class="col-12 mb-1">
                        <label class="form-label" for="receivedIn"> Transfer To / Received In </label>
                        <v-select
                            v-model="formData.receive_in_hand"
                            :options="accountHeads"
                            label="name"
                            placeholder="Select Account Head"
                        />
                    </div>

                    <div class="col-12 mb-1">
                      <label for="attachment" class="form-label fw-bold">Attachment</label>
                      <div class="attachment-container">
                        <BlobFileViewer :attachments="formData.attachments" />
                        <FileUpload
                            :is-drag-active="true"
                            button-text="Upload Documents"
                            v-model="formData.attachments"
                        />
                      </div>
                    </div>

                    <div class="col-12 col-md-6 mb-1">
                      <label class="form-label" for="amount"> <span class="required">*</span> Amount </label>
                      <input
                          class="form-control text-end"
                          type="number"
                          v-model="formData.amount"
                          placeholder="0.00"
                      >
                    </div>

                    <div class="col-12 col-md-6 mb-1">
                        <div class="d-flex align-items-center justify-content-between filter-label">
                            <label for="colFormLabel" class="form-label">Party: </label>
                        </div>
                        <AsyncSelect
                            placeholder="Select Party"
                            v-model="contactProfile"
                            :api-service="fetchContactProfiles"
                            :format-label="formatPatientLabel"
                        />
                    </div>

                    <div class="col-12 mb-1 mt-1">
                      <label class="form-label" for="note"> Note </label>
                      <textarea
                          class="form-control"
                          v-model="formData.note"
                          rows="2"
                          placeholder="Note"
                      ></textarea>
                    </div>

                    <div class="col-12 text-center mb-2 mt-2">
                      <button :disabled="loading" @click="submit" type="button" class="btn btn-primary full-width me-1 waves-effect waves-float waves-light">
                        <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
            </div>
        </div>
        <Loader v-if="loader"/>
    </div>
</template>

<script setup>
import {inject, onMounted, ref} from 'vue';
import {useRoute} from 'vue-router';
import handleContra from '@/services/modules/contra';
import handlePayment from '@/services/modules/payment';
import handleContact from "@/services/modules/contact";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import FileUpload from "@/components/atom/FileUpload.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import BlobFileViewer from "@/components/atom/BlobFileViewer.vue";
import Loader from "@/components/atom/LoaderComponent.vue";

const props = defineProps({
    headId: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['close', 'submit']);

const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { createContra, loading } = handleContra();
const { fetchCashAndBank } = handlePayment();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();

const loader = ref(false);
const modalRef = ref(null);
const accountHeads = ref([]);
const contactProfile = ref(null);
const formData = ref({
    date: new Date().toISOString().split('T')[0],
    amount: null,
    receive_in_hand: null,
    note: '',
    attachments: []
});

const onClickClose = () => {
    emit('close');
};

const getAccountHeads = async () => {
    try {
        loader.value = true;
        const res = await fetchCashAndBank(route.params.companyId);
        if (res.status) {
            accountHeads.value = res.data;
        }
    } catch (err) {
        showError("Failed to load account heads");
    } finally {
        loader.value = false;
    }
};

const submit = async () => {
    if (!formData.value.date) {
        showError("Please select date");
        return;
    }
    if (!formData.value.amount || formData.value.amount <= 0) {
        showError("Please enter a valid amount");
        return;
    }
    if (!formData.value.receive_in_hand) {
        showError("Please select Transfer To head");
        return;
    }

    const payload = new FormData();
    payload.append('company_id', route.params.companyId);
    payload.append('date', formData.value.date);
    payload.append('note', formData.value.note);
    payload.append('mop_references', 'mop');
    payload.append('voucher_type', 'contra_voucher');
    payload.append('txn_type', 'general_contra');

    const generalLedgers = [];
    
    let contactId = null;
    if (contactProfile.value) {
      contactId = contactProfile.value.id;
    }

    generalLedgers.push({
      account_head_id: props.headId.id,
      debit: null,
      credit: formData.value.amount,
      note: formData.value.note,
      mop_account_head_id: formData.value.receive_in_hand ? formData.value.receive_in_hand.id : null,
      contact_profile_id: contactId,
      business_id: null,
      project_id: null,
      donor_id: null,
      cost_centre_id: null,
      tag_register_id: null,
      tax_rate: null,
      taxable_amount: null
    });

    generalLedgers.push({
      account_head_id: formData.value.receive_in_hand ? formData.value.receive_in_hand.id : null,
      debit: formData.value.amount,
      credit: null,
      note: formData.value.note,
      mop_account_head_id: props.headId.id,
      contact_profile_id: contactId,
      business_id: null,
      project_id: null,
      donor_id: null,
      cost_centre_id: null,
      tag_register_id: null,
      tax_rate: null,
      taxable_amount: null
    });

    payload.append('general_ledgers', JSON.stringify(generalLedgers));

    if (formData.value.attachments && formData.value.attachments.length > 0) {
        for (let i = 0; i < formData.value.attachments.length; i++) {
            payload.append(`attachments[${i}]`, formData.value.attachments[i]);
        }
    }

    try {
        const res = await createContra(payload);
          if (!res.status) {
            return showError(res.message);
          }
          showSuccess(res.message);
          emit('submit');
          emit('close');
    } catch (err) {
        showError("Something went wrong. Check your connectivity!!");
    }
};

onMounted(() => {
    getAccountHeads();
});
</script>

<style scoped>
.required {
  color: red;
}
.full-width {
    width: 100%;
}
.attachment-container {
  background: #f8f9fa;
  border: 2px dashed #d8dbe0;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}
.attachment-container:hover {
  border-color: #7367f0;
  background: #f0f0ff;
}
@media (max-width: 576px) {
  .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }
  .table-responsive {
    font-size: 0.85rem;
  }
}
</style>
